/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { computed, action, set } from '@ember/object';
import { debounce } from '@ember/runloop';
import { tracked } from '@glimmer/tracking';
import { TaskInstance } from 'ember-concurrency';
import RouterService from '@ember/routing/router-service';
import config from 'cio/config/environment';
import AutosuggestService from 'cio/services/autosuggest';
import EnvironmentService from 'cio/services/environment';
import AttributeCondition from 'cio/app-models/conditions/attribute';
import EventAttributeCondition from 'cio/app-models/conditions/event-attribute';
import CollectionAttributeCondition from 'cio/app-models/conditions/collection-attribute';
import AnalyticsService from 'cio/services/analytics';
import CampaignModel from 'cio/models/campaign';
import { isEmpty } from '@ember/utils';
import { ConditionType } from 'cio/utils/condition-types';
import ObjectAttributeCondition, { ObjectAttributeSource } from 'cio/app-models/conditions/object-attribute';

interface NameArgs {
  controlGroupClassNames?: string;
  inputClassNames?: string;
  attributeNames: string[];
  condition: AttributeCondition | EventAttributeCondition | CollectionAttributeCondition | ObjectAttributeCondition;
  placeholder?: string;
  size?: string;
  autosuggestValues: string[];
  disabled?: boolean;
  onInput: (value: string | null) => unknown;
  campaign?: CampaignModel;
  shouldShowAutosuggestValues?: boolean;
  isObject?: boolean;
}

const ERROR_SOURCE_NAME_MAP: Record<string, string> = {
  'segments.segment.overview.edit': 'Segment Edit Page',
  'segments.new': 'Segment Setup Page',
  'campaigns.campaign.setup.workflow.actions': 'True-False Branch',
  'broadcasts.newsletter.setup.recipients.index': 'Newsletter Recipients Setup Page',
  'people.list.index': 'People Page',
};

export default class Name extends Component<NameArgs> {
  @service autosuggest!: AutosuggestService;
  @service environment!: EnvironmentService;
  @service analytics!: AnalyticsService;
  @service router!: RouterService;

  @tracked hasObserver = false;
  @tracked attributesTaskInstance: TaskInstance<unknown> | null = null;
  // Prevent the same analytics event from firing when errors are re-computed
  @tracked trackedErrors: string[] = [];

  get shouldShowAutosuggestValues() {
    return this.args.shouldShowAutosuggestValues ?? true;
  }

  get size() {
    if (this.args.size === 'sm') {
      return 'small';
    }

    return this.args.size ?? 'normal';
  }

  @computed('args.{placeholder,condition.type}')
  get placeholder() {
    if (this.args.placeholder) {
      return this.args.placeholder;
    }

    if (this.args.condition.type === ConditionType.CollectionAttribute) {
      return 'collection attribute';
    }

    if (this.args.condition.type === ConditionType.ObjectAttribute) {
      return 'object attribute';
    }

    if (
      this.args.condition.type === ConditionType.EventAttribute ||
      this.args.condition.type === ConditionType.ForeignEventAttribute
    ) {
      if (this.args.campaign?.isWebhookTriggered) {
        return 'trigger attribute';
      }

      return 'event_attribute_name';
    }

    return 'profile attribute';
  }

  @computed('args.{condition.type,attributeNames.[]}', 'attributeNames.[]', 'attributesTaskInstance.value')
  get autocompleteItems() {
    if (
      this.args.condition?.type === ConditionType.EventAttribute ||
      this.args.condition?.type === ConditionType.ForeignEventAttribute
    ) {
      return (this.attributesTaskInstance?.value as string[] | undefined) ?? [];
    }

    if (this.args.condition?.type === ConditionType.CollectionAttribute) {
      // @ts-ignore - this doesn't seem to be valid via types, but I don't want to remove it. Should be validated
      return (this.args.condition as CollectionAttributeCondition).collectionSchema ?? [];
    }

    if (this.args.condition?.type === ConditionType.Attribute || this.args.attributeNames?.length > 0) {
      return this.args.attributeNames;
    }

    return [];
  }

  @computed('args.condition.objectAttributeSource')
  get hideAttributeLabel() {
    return (
      (this.args.condition as ObjectAttributeCondition).objectAttributeSource ===
        ObjectAttributeSource.RelationshipAttribute ?? false
    );
  }

  @computed('args.{autoSuggestValues,condition.type}')
  get shouldLoadAutoSuggestValues() {
    return (
      this.args.autosuggestValues &&
      (this.args.condition.type === ConditionType.EventAttribute ||
        this.args.condition.type === ConditionType.ForeignEventAttribute)
    );
  }

  @action
  loadAutosuggestAttributes() {
    if (this.shouldLoadAutoSuggestValues && 'event' in this.args.condition) {
      if (this.args.campaign?.isWebhookTriggered) {
        this.attributesTaskInstance = this.autosuggest.loadWebhookCampaignWebhookKeys.perform(this.args.campaign);
      } else if (!isEmpty(this.args.condition.event)) {
        this.attributesTaskInstance = this.autosuggest.loadEventKeys.perform(this.args.condition.event);
      }
    } else {
      this.attributesTaskInstance = null;
    }
  }

  @action
  onConditionEventChange() {
    if (!this.shouldLoadAutoSuggestValues) {
      return;
    }

    debounce(this, this.loadAutosuggestAttributes, config.APP.autosuggestDebounceDuration);
  }

  @action
  trackErrorChange(_: HTMLElement, [errors]: [string[]?]) {
    if (errors?.length && !this.trackedErrors.includes(errors[0])) {
      this.trackedErrors = [...this.trackedErrors, errors[0]];

      this.analytics.track('user', 'error_encountered', {
        currentUrl: this.router.currentURL,
        message: errors[0],
        source: ERROR_SOURCE_NAME_MAP[this.router.currentRouteName],
      });
    }
  }

  @action
  onChange(selection: string | Record<string, any> | null) {
    if (!selection || typeof selection === 'string') {
      set(this.args.condition, 'name', selection ?? '');
      return this.args.onInput(selection);
    }

    set(this.args.condition, 'name', selection.value || '');
    return this.args.onInput(selection.value);
  }

  @action
  onCampaignLatestWebhookChanged() {
    if (this.args.campaign?.isWebhookTriggered && this.args.campaign?.latestWebhookLog != null) {
      this.loadAutosuggestAttributes();
    }
  }
}
